import { Content, DefaultPage } from "@shared/layout/DefaultPage";
import { Heading } from "@ui/Heading";
import FatArrow from "@assets/icons/FatBottomArrow.svg";
import TisitLogo from "@assets/icons/tisit_logo_full.svg";
import { Centered } from "@shared/layout/Centered";
import {
  heightSchema,
  nameSchema,
  surnameSchema,
  UserGender,
  weightSchema,
} from "@entities/User";
import { useEffect, useRef, useState } from "react";
import { YellowFilledButton } from "@shared/styles/YellowFilledButton";
import { useUserService } from "@entities/User";
import { isUserFullSetup } from "@entities/User/lib/parsers/isUserFullSetup";
import { useIonRouter } from "@ionic/react";
import { ROUTES } from "@shared/constants/routes";
import { toast } from "sonner";
import { observer } from "mobx-react";
import { CustomLoader } from "@/shared/animations/loaders/CustomLoader";
import { UserService } from "@/entities/User/model/service";
import { CalendarDays, Ruler, Scale, User } from "lucide-react";
import { consumeProgramAuthReturnPath } from "@shared/lib/authRedirect";
import { useQueryClient } from "@tanstack/react-query";
import { userQueryKeys } from "@entities/User/config/query-keys";
import { useLocation } from "react-router-dom";
import {
  clampDay,
  createNumberOptions,
  DateWheelPicker as SharedDateWheelPicker,
  formatDisplayDate,
  GenderSwitchCard as SharedGenderSwitchCard,
  InfoBottomSheet as SharedInfoBottomSheet,
  InfoField as SharedInfoField,
  normalizeDateParts,
  ProfileInfoForm as SharedProfileInfoForm,
  TextSheetInput as SharedTextSheetInput,
  toIsoDate,
  WeightScalePicker as SharedWeightScalePicker,
  WheelPicker as SharedWheelPicker,
} from "@shared/ui/ProfileInfoForm";

const LoadingScreen = () => <CustomLoader />;
const PROFILE_WAIT_TIMEOUT_MS = 3000;
const WEIGHT_MIN = 30;
const WEIGHT_MAX = 250;
const WEIGHT_STEP = 0.1;

type EditableField = "name" | "surname" | "height" | "weight" | "birthday";

const getFieldTitle = (field: EditableField): string => {
  const titles: Record<EditableField, string> = {
    name: "Введите имя",
    surname: "Введите фамилию",
    height: "Укажите рост",
    weight: "Текущий вес",
    birthday: "Выберите дату рождения",
  };

  return titles[field];
};

export const AuthInfoPage = observer(() => {
  const userService = useUserService() as UserService;
  const { data: userData, updateData } = userService;
  const queryClient = useQueryClient();
  const nav = useIonRouter();
  const location = useLocation();

  const [name, setName] = useState<string>("");
  const [surname, setSurname] = useState<string>("");
  const [height, setHeight] = useState<string>("");
  const [weight, setWeight] = useState<string>("");
  const [birthday, setBirthday] = useState<string>("");
  const [sex, setSex] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeField, setActiveField] = useState<EditableField | null>(null);
  const [draftValue, setDraftValue] = useState<string>("");
  const [dateDraft, setDateDraft] = useState(normalizeDateParts(""));
  const postAuthRedirectPathRef = useRef<string | null>(null);
  const hasRedirectedRef = useRef(false);

  const getPostAuthRedirectPath = (): string => {
    if (!postAuthRedirectPathRef.current) {
      postAuthRedirectPathRef.current =
        consumeProgramAuthReturnPath() ?? ROUTES.overall.main;
    }

    return postAuthRedirectPathRef.current;
  };

  useEffect(() => {
    const isActiveAuthInfoRoute =
      location.pathname === ROUTES.auth.enterBaseInfo;
    const isUserFullSetUp = isUserFullSetup(userData);
    const redirectAfterAuthInfo = () => {
      if (!isActiveAuthInfoRoute || hasRedirectedRef.current) {
        return;
      }

      hasRedirectedRef.current = true;
      nav.push(getPostAuthRedirectPath());
    };

    const timer = window.setTimeout(() => {
      if (isUserFullSetUp) {
        redirectAfterAuthInfo();
        return;
      }

      setName(userData.firstName || "");
      setSurname(userData.lastName || "");
      setHeight(userData.height ? String(userData.height) : "");
      setWeight(userData.weight ? String(userData.weight) : "");
      setSex(userData.sex || "");
      setBirthday(userData.birthday || "");
      setIsLoading(false);
    }, PROFILE_WAIT_TIMEOUT_MS);

    if (userService.hasResolvedProfile) {
      window.clearTimeout(timer);

      if (isUserFullSetUp) {
        redirectAfterAuthInfo();
        return;
      }

      setName(userData.firstName || "");
      setSurname(userData.lastName || "");
      setHeight(userData.height ? String(userData.height) : "");
      setWeight(userData.weight ? String(userData.weight) : "");
      setSex(userData.sex || "");
      setBirthday(userData.birthday || "");
      setIsLoading(false);
    }

    return () => window.clearTimeout(timer);
  }, [location.pathname, nav, userData, userService]);

  const openEditor = (field: EditableField) => {
    setActiveField(field);

    if (field === "name") {
      setDraftValue(name);
    }

    if (field === "surname") {
      setDraftValue(surname);
    }

    if (field === "height") {
      setDraftValue(height || "180");
    }

    if (field === "weight") {
      setDraftValue(weight || "75.0");
    }

    if (field === "birthday") {
      setDateDraft(normalizeDateParts(birthday));
    }
  };

  const closeEditor = () => {
    setActiveField(null);
    setDraftValue("");
  };

  const commitEditor = () => {
    if (!activeField) {
      return;
    }

    const trimmedValue = draftValue.trim();

    if (activeField === "name") {
      const parsed = nameSchema.safeParse(trimmedValue);
      if (!parsed.success) {
        toast.error(parsed.error.errors[0].message);
        return;
      }
      setName(trimmedValue);
    }

    if (activeField === "surname") {
      const parsed = surnameSchema.safeParse(trimmedValue);
      if (!parsed.success) {
        toast.error(parsed.error.errors[0].message);
        return;
      }
      setSurname(trimmedValue);
    }

    if (activeField === "height") {
      const parsed = heightSchema.safeParse(trimmedValue);
      if (!parsed.success) {
        toast.error(parsed.error.errors[0].message);
        return;
      }
      setHeight(trimmedValue);
    }

    if (activeField === "weight") {
      const parsed = weightSchema.safeParse(trimmedValue);
      if (!parsed.success) {
        toast.error(parsed.error.errors[0].message);
        return;
      }
      setWeight(trimmedValue);
    }

    if (activeField === "birthday") {
      const day = clampDay(dateDraft.day, dateDraft.month, dateDraft.year);
      setBirthday(toIsoDate(day, dateDraft.month, dateDraft.year));
    }

    closeEditor();
  };

  const handleSave = async () => {
    if (
      name === "" ||
      surname === "" ||
      height === "" ||
      weight === "" ||
      birthday === "" ||
      sex === ""
    ) {
      toast.error("Необходимо заполнить все поля");
      return;
    }

    const profileData = {
      firstName: name,
      weight: +weight,
      height: +height,
      lastName: surname,
      sex: sex as UserGender,
      birthday: birthday,
    };

    await queryClient.cancelQueries({ queryKey: userQueryKeys.profile });
    const response: boolean = await updateData(profileData);

    if (response) {
      userService.setData(profileData);
      queryClient.setQueriesData(
        { queryKey: userQueryKeys.profile },
        (cachedProfile: unknown) => ({
          ...(typeof cachedProfile === "object" && cachedProfile !== null
            ? cachedProfile
            : {}),
          ...userService.data,
          ...profileData,
        }),
      );
      hasRedirectedRef.current = true;
      nav.push(getPostAuthRedirectPath());
    }
  };

  const setNumericDraft = (value: number) => {
    setDraftValue(String(value));
  };

  const renderEditorContent = () => {
    if (activeField === "name" || activeField === "surname") {
      return (
        <SharedTextSheetInput
          placeholder={
            activeField === "name" ? "Введите имя" : "Введите фамилию"
          }
          value={draftValue}
          onChange={setDraftValue}
        />
      );
    }

    if (activeField === "height") {
      const current = Number(draftValue || 180);
      const min = 120;
      const max = 230;
      const value = Math.min(Math.max(current, min), max);
      const heightOptions = createNumberOptions(min, max);

      return (
        <div className="auth-info-height-picker">
          <SharedWheelPicker
            ariaLabel="Выбор роста"
            className="auth-info-number-wheel"
            options={heightOptions}
            value={value}
            onChange={setNumericDraft}
          />
          <span className="auth-info-height-unit">см</span>
        </div>
      );
    }

    if (activeField === "weight") {
      const current = Number(draftValue || 75);
      const value = Math.min(Math.max(current, WEIGHT_MIN), WEIGHT_MAX);

      return (
        <SharedWeightScalePicker
          value={value}
          min={WEIGHT_MIN}
          max={WEIGHT_MAX}
          step={WEIGHT_STEP}
          onChange={(nextValue) => setDraftValue(nextValue.toFixed(1))}
        />
      );
    }

    if (activeField === "birthday") {
      return (
        <SharedDateWheelPicker value={dateDraft} onChange={setDateDraft} />
      );
    }

    return null;
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <DefaultPage className="auth-info-page">
      <Content className="auth-info-content">
        <div className="auth-info-header">
          <button
            className="auth-info-back"
            type="button"
            aria-label="Назад"
            onClick={() => nav.goBack()}
          ></button>
          <Centered>
            <Heading weight={700}>Регистрация</Heading>
          </Centered>
        </div>

        <Centered className="auth-info-brand">
          <img src={FatArrow} alt="Продолжение регистрации" />
          <img src={TisitLogo} alt="TISIT" className="auth-info-logo" />
          <span>
            Ваш персональный,
            <br />
            карманный тренер
          </span>
        </Centered>

        <SharedProfileInfoForm>
          <SharedInfoField
            icon={<User />}
            label="Имя"
            value={name}
            placeholder=""
            onClick={() => openEditor("name")}
            onClear={() => setName("")}
          />
          <SharedInfoField
            icon={<User />}
            label="Фамилия"
            value={surname}
            placeholder=""
            onClick={() => openEditor("surname")}
            onClear={() => setSurname("")}
          />
          <SharedInfoField
            icon={<Ruler />}
            label="Рост"
            value={height}
            placeholder=""
            onClick={() => openEditor("height")}
            onClear={() => setHeight("")}
          />
          <SharedInfoField
            icon={<Scale />}
            label="Вес"
            value={weight}
            placeholder=""
            onClick={() => openEditor("weight")}
            onClear={() => setWeight("")}
          />
          <SharedInfoField
            icon={<CalendarDays />}
            label="Дата рождения"
            value={formatDisplayDate(birthday)}
            placeholder=""
            onClick={() => openEditor("birthday")}
            onClear={() => setBirthday("")}
          />
          <SharedGenderSwitchCard value={sex} onChange={setSex} />
        </SharedProfileInfoForm>
        <div className="auth-info-actions">
          <YellowFilledButton
            onClick={handleSave}
            className="auth-info-submit"
            style={{ fontWeight: 400 }}
          >
            Зарегистрироваться
          </YellowFilledButton>
        </div>
      </Content>

      {activeField && (
        <SharedInfoBottomSheet
          title={getFieldTitle(activeField)}
          modifier={activeField}
          onClose={closeEditor}
          onCommit={commitEditor}
        >
          {renderEditorContent()}
        </SharedInfoBottomSheet>
      )}
    </DefaultPage>
  );
});
